// Modul 40 B2 — die reinen Helfer der Board-Ansicht.
//
// Hier steht bewusst nichts, was rendert oder ruft: Zuordnung von Dokumenten zu Zeilen,
// die Termin-Leiste eines Containers, die Summen des Kartenkopfs und die zwei Umrechner
// (Berliner Kalendertag, Betrags-Eingabe). Alles davon ist ohne Provider, ohne Attrappen
// und ohne Uhr pruefbar — deshalb liegt es getrennt von den Komponenten.
import type {
  DeliveryConfirmationResult,
  PurchaseDocument,
  PurchaseOrderListRow,
} from "@/api/purchase";

export type ZeilenTyp = "invoice" | "packliste" | "spedition" | "zoll" | "sonstige";

/**
 * Dokumenttyp (Wire) → Zeile der Karte.
 *
 * `invoice_packing_combo` ist eine Datei, die Rechnung UND Packliste enthaelt; sie gehoert
 * in die Invoice-Zeile, weil dort danach gesucht wird. `typ` ist im Wire-Typ nullbar (der
 * Agent hat das Dokument noch nicht eingeordnet) — solche Dateien landen unter "Sonstige"
 * und nicht im Nichts.
 *
 * Wer den Rumpf anfasst: der `null`-Riegel steht fuer den TYP (in einen
 * `Record<string, …>` darf man nicht mit `null` greifen). Getragen wird das Verhalten vom
 * `?? "sonstige"` — ein unbekannter Schluessel und `null` liefern beide `undefined`.
 */
const ZUORDNUNG: Record<string, ZeilenTyp> = {
  commercial_invoice: "invoice",
  invoice_packing_combo: "invoice",
  packing_list: "packliste",
  spediteur_rechnung: "spedition",
  zollbeleg: "zoll",
};

export function zeilenTypFuerDokument(typ: string | null): ZeilenTyp {
  if (typ === null) return "sonstige";
  return ZUORDNUNG[typ] ?? "sonstige";
}

/**
 * Alle Dokumente einer Bestellung, nach Zeilen der Karte sortiert.
 *
 * Der Sonderfall in der Mitte ist der Grund, warum diese Funktion mehr als eine Zeile ist:
 * eine Spediteur-Rechnung oder ein Zollbeleg **ohne** Container (`purchase_shipment_id`
 * ist `null`) kommt in keiner Container-Zeile vor — die filtert auf die Container-Id
 * (`dokumenteFuerContainer`). Bis zum Whole-Branch-Review landeten solche Dateien im
 * Eimer `spedition`/`zoll`, den die Karte gar nicht rendert: die Datei war da, aber
 * nirgends zu sehen und nicht herunterzuladen (Befund Reviewer 1). Sie gehoert deshalb
 * unter "Sonstige" — sichtbar, mit dem Hinweis, dass ihr die Zuordnung fehlt.
 */
export function dokumenteJeZeile(docs: PurchaseDocument[]): Record<ZeilenTyp, PurchaseDocument[]> {
  const out: Record<ZeilenTyp, PurchaseDocument[]> = {
    invoice: [], packliste: [], spedition: [], zoll: [], sonstige: [],
  };
  for (const d of docs) {
    const typ = zeilenTypFuerDokument(d.typ);
    const ohneContainer = (typ === "spedition" || typ === "zoll" || typ === "packliste")
      && d.purchase_shipment_id === null;
    out[ohneContainer ? "sonstige" : typ].push(d);
  }
  return out;
}

/**
 * Dokumente EINES Containers.
 *
 * Der Filter auf `purchase_shipment_id` ist der ganze Punkt: eine Spediteur-Rechnung, die
 * an keinem Container haengt (`null`), gehoert an keinen — sonst zeigten alle Container
 * dieselbe Rechnung und saehen abgerechnet aus.
 */
export function dokumenteFuerContainer(
  docs: PurchaseDocument[], shipmentId: number, typ: "spedition" | "zoll" | "packliste",
): PurchaseDocument[] {
  return docs.filter((d) => d.purchase_shipment_id === shipmentId && zeilenTypFuerDokument(d.typ) === typ);
}

/** Fachlicher Klartext fuer das Ergebnis der kombinierten Zustell-/WE-Aktion. */
export function zustellErgebnisText(result: DeliveryConfirmationResult): string {
  if (result.outcome === "receipt_ready") {
    return `Zustellung bestätigt; WE #${result.receipt_id} ${result.already_linked ? "war bereits verknüpft" : "wurde angelegt"}.`;
  }
  if (result.reason === "no_items") {
    return "Zustellung gespeichert; der Wareneingang bleibt offen, weil der Container noch keine Positionen enthält.";
  }
  if (result.reason === "unlinked_items") {
    const count = result.count ?? 0;
    return `Zustellung gespeichert; der Wareneingang bleibt offen, weil ${count} Containerposition${count === 1 ? "" : "en"} keinem Grundartikel zugeordnet ${count === 1 ? "ist" : "sind"}.`;
  }
  return "Zustellung gespeichert; der Wareneingang bleibt offen, weil mindestens ein Grundartikel technisch nicht auflösbar ist.";
}

export interface TerminSchritt {
  key: "crd" | "etd" | "eta_pod" | "eta_door";
  label: "CRD" | "ETD" | "ETA" | "Door";
  datum: string | null;
  erreicht: boolean;
}

/**
 * Die vier Termine eines Containers in fester Reihenfolge CRD → ETD → ETA → Door.
 *
 * `erreicht` heisst: das Datum ist gesetzt UND liegt nicht in der Zukunft (Tagesvergleich,
 * einschliesslich heute). Der Vergleich laeuft auf den ersten zehn Zeichen, weil die
 * Wire-Felder Text sind und eine Uhrzeit mitkommen kann — ohne den Schnitt waere
 * "2026-08-18T09:00:00Z" groesser als "2026-08-18" und der heutige Schritt fiele zurueck.
 * `heute` kommt von aussen (`heuteBerlin()`), damit diese Funktion ohne Uhr pruefbar ist.
 */
export function terminLeiste(
  s: { crd: string | null; etd: string | null; eta_pod: string | null; eta_door: string | null },
  heute: string,
): TerminSchritt[] {
  const schritte: Array<[TerminSchritt["key"], TerminSchritt["label"]]> = [
    ["crd", "CRD"], ["etd", "ETD"], ["eta_pod", "ETA"], ["eta_door", "Door"],
  ];
  return schritte.map(([key, label]) => {
    const roh = s[key];
    const datum = roh ? roh.slice(0, 10) : null;
    return { key, label, datum, erreicht: datum !== null && datum <= heute };
  });
}

/**
 * Die zwei gerechneten Zahlen des Kartenkopfs.
 *
 * `offenUsd` entsteht NUR, wenn beide Werte da sind. Fehlt die Rechnungssumme, ist "offen"
 * schlicht unbekannt — eine Rechnung mit 0 anzusetzen ergaebe einen negativen Betrag und
 * damit eine Zahl, die es nicht gibt. `kostenEur` bleibt `null`, wenn das Feld fehlt (eine
 * aeltere Backend-Fassung im Deploy-Fenster) — "nicht gemeldet" ist nicht "null Euro".
 */
export function kartenSummen(row: PurchaseOrderListRow): { offenUsd: number | null; kostenEur: number | null } {
  const rechnung = row.invoice_total_usd;
  const bezahlt = row.paid_usd;
  const offenUsd =
    rechnung === null || rechnung === undefined || bezahlt === null || bezahlt === undefined
      ? null
      : rechnung - bezahlt;
  return { offenUsd, kostenEur: row.costs_eur_total ?? null };
}

/**
 * Der heutige Kalendertag in Berlin als `YYYY-MM-DD`.
 *
 * Frontend-Gegenstueck zu `berlinToday()` im Backend. Bewusst NICHT `toISOString()`: das
 * liefert UTC, und zwischen Mitternacht und 02:00 Berliner Zeit waere das der Vortag — die
 * Termin-Leiste zeigte einen heute frueh eingetroffenen Container als "noch offen".
 * `sv-SE` ist der kuerzeste Weg zu ISO-Schreibweise aus `toLocaleDateString`.
 */
export function heuteBerlin(now: Date = new Date()): string {
  return now.toLocaleDateString("sv-SE", { timeZone: "Europe/Berlin" });
}

/**
 * Betrags-Eingabe einer Inline-Zelle → Zahl.
 *
 * Drei Ergebnisse, die die Zelle unterscheiden muss:
 *   `null`      leeren (die Eingabe war leer)  — eine Absicht
 *   `undefined` ungueltig                      — ein Vertipper, es wird NICHT gespeichert
 *   Zahl        speichern
 *
 * Die Trennung ist der Kern: `Number("abc")` ist `NaN`, und ein `NaN` liefe als `null`
 * durch — die Zelle wuerde den Wert LOESCHEN, obwohl sich jemand vertippt hat.
 *
 * Schreibweisen: mit Komma gilt deutsch (Punkte sind Tausender), ohne Komma gilt ein Punkt
 * als Dezimalpunkt — ausser die Form ist reine Tausender-Gruppierung (`1.234`, `1.234.567`).
 * Mehr als DREI Nachkommastellen werden abgewiesen, egal auf welcher Seite: `1,2345` wurde
 * vorher still zu 1,23 gerundet, und eine still gekuerzte Geldzahl ist schlimmer als eine
 * abgewiesene Eingabe.
 *
 * `1.234` bleibt bewusst die deutsche Tausender-Form (= 1234) und nicht die englische
 * Dezimalform (= 1,234). Reviewer 2 hat die Zweideutigkeit aufgezeigt; sie ist echt und
 * nicht aufloesbar. Entschieden wird sie zugunsten des Hauses: die Oberflaeche ist deutsch
 * (Platzhalter "z. B. 1.234,50"), und ein Geldbetrag mit genau drei Nachkommastellen ist
 * kein Rechnungsbetrag. Wer englisch schreibt, tippt zwei Nachkommastellen — und die
 * Form `1.23` ist eindeutig.
 *
 * Gerundet wird auf zwei Nachkommastellen, weil die Spalte in der Datenbank zwei fuehrt;
 * ungerundet kaeme der Wert beim naechsten Laden anders zurueck, als er eingegeben wurde.
 */
export function parseBetrag(s: string): number | null | undefined {
  const roh = s.trim();
  if (roh === "") return null;
  // Nur Ziffern, Punkt, Komma. Der Riegel ist NICHT gegen "abc" (das faellt unten als NaN
  // durch), sondern gegen die Zahlen-Schreibweisen von JavaScript: `Number("0x10")` ist 16
  // und `Number("1e3")` ist 1000. Ohne ihn wuerde ein Vertipper still zu einem Betrag.
  if (!/^[0-9.,]+$/.test(roh)) return undefined;

  // Die Schreibweise muss GANZ passen. Die erste Fassung entfernte im deutschen Zweig
  // einfach alle Punkte — damit wurde aus dem Vertipper "12.34,56" klaglos 1.234,56, also
  // das Hundertfache. Beide Reviewer haben das gefunden; ein Betrag, der still um eine
  // Stelle springt, ist schlimmer als eine abgewiesene Eingabe.
  let normal: string;
  if (roh.includes(",")) {
    // deutsch: entweder mit Dreiergruppen (1.234,50) oder ganz ohne Punkte (1234,50)
    if (!/^\d{1,3}(\.\d{3})*,\d{1,3}$/.test(roh) && !/^\d+,\d{1,3}$/.test(roh)) return undefined;
    normal = roh.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(roh)) {
    normal = roh.replace(/\./g, "");            // 1.234.567 = deutsche Tausender
  } else if (roh.includes(".")) {
    if (!/^\d+\.\d{1,3}$/.test(roh)) return undefined;
    normal = roh;                               // 1234.50 = englische Schreibweise
  } else {
    normal = roh;
  }

  const wert = Number(normal);
  if (!Number.isFinite(wert) || wert < 0) return undefined;
  return aufCent(normal);   // undefined, wenn der Betrag nicht mehr centgenau darstellbar ist
}

/**
 * Auf Cent runden — aus der ZIFFERNFOLGE, nicht aus der Gleitkommazahl.
 *
 * `Math.round(1.005 * 100) / 100` ergibt 1.00, weil 1.005 binaer knapp darunter liegt.
 * Der oft empfohlene `Number.EPSILON`-Zuschlag rettet genau diesen Fall — aber nicht die
 * Klasse: `Math.round((2.135 + Number.EPSILON) * 100) / 100` ist 2.13 (nachgemessen mit
 * node, 2026-08-18). Welche Betraege er verfehlt, haengt an der Groessenordnung und laesst
 * sich nicht aufzaehlen; man kennt immer nur die zufaellig getesteten.
 *
 * Deshalb wird hier die dritte Nachkommastelle der EINGABE gelesen und kaufmaennisch
 * aufgerundet: das Ergebnis haengt an dem, was der Nutzer getippt hat, und nicht daran, auf
 * welche Seite die binaere Naeherung faellt.
 */
function aufCent(normal: string): number | undefined {
  const [ganz, dezRoh = ""] = normal.split(".");
  const dez = dezRoh.padEnd(3, "0");
  const cents = Number(ganz) * 100 + Number(dez.slice(0, 2)) + (Number(dez[2]) >= 5 ? 1 : 0);
  // Jenseits von `Number.MAX_SAFE_INTEGER` gibt es nicht mehr jeden Cent; die Rechnung
  // liefert dort still einen NACHBARWERT. Bei Geld ist eine abgewiesene Eingabe besser als
  // ein veraenderter Betrag (Befund Reviewer 1) — gemeint sind Betraege ab rund
  // 90 Billionen, also nichts, was jemand tippt, sondern verrutschte Zwischenablage.
  if (!Number.isSafeInteger(cents)) return undefined;
  return cents / 100;
}
